import React, { useState, useEffect, useRef } from 'react';
import { FileText, Mail, MessageSquare, HeartHandshake, Calendar, Rocket, ChevronDown, ChevronRight, Plus, Trash2, Image } from 'lucide-react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import RichEmailEditor, {
  buildTableHtml, sanitizePastedHtml, handleRichPaste, stripEditorChrome,
  alignImage, joinPreviousLine, moveToOwnLine, setImageWidth,
  makeRowFromImage, makeRowFromImageList, imagesInSelection, moveImage,
  tableFromSelection, tableHasBorders, setTableBorders, renderEmailBody,
  linkImage, imageLinkHref
} from './RichEmailEditor';
// Pasting a signature must always work, whatever it weighs — see utils/emailImages.
import { handleRichPasteWithImages, slimEmailBody } from '../utils/emailImages';

// Email style options (same as Send Emails)
const EMAIL_FONT_OPTIONS = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' }
];
const EMAIL_FONT_SIZE_OPTIONS = ['12px', '14px', '16px', '18px'];
const EMAIL_LINE_HEIGHT_OPTIONS = ['1.2', '1.4', '1.5', '1.6', '1.8'];
const DEFAULT_EMAIL_STYLE = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '14px',
  lineHeight: '1.5',
  textColor: '#333333'
};

// Escape HTML for safe insertion
const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Plain text to HTML (for display in contenteditable)
const plainToHtml = (text) => {
  if (!text || typeof text !== 'string') return '<p><br></p>';
  const isHtml = text.includes('<');
  if (isHtml) return text;
  return (text || '').split('\n').map(p => {
    const t = p.trim();
    return t ? `<p>${escapeHtml(t)}</p>` : '<p><br></p>';
  }).join('');
};

const STAGE_OPTIONS = [
  'Initial Sales Pitch',
  'Follow-up 1',
  'Follow-up 2',
  'Follow-up 3',
  'Deal Confirmation',
  'Meeting Scheduling Calls',
  'Onboarding Emails'
];

const STAGE_ICONS = {
  'Initial Sales Pitch': Mail,
  'Follow-up 1': MessageSquare,
  'Follow-up 2': MessageSquare,
  'Follow-up 3': MessageSquare,
  'Deal Confirmation': HeartHandshake,
  'Meeting Scheduling Calls': Calendar,
  'Onboarding Emails': Rocket
};

// Same defaults as Send Emails – so Templates page shows the actual message used when nothing is saved
const DEFAULT_SUBJECT = 'Follow-up - {{company_name}}';
const DEFAULT_BODIES = {
  'Initial Sales Pitch': `Hello {{first_name}},

At American Green Solutions (AGS), we deliver AI-powered performance monitoring and APO services that act as a seamless extension of your existing operations team during off-hours. Our coverage includes 24/7/365 weekends, and holidays, when internal staffing is often limited or cost intensive.

By combining advanced AI analytics with experienced APO analysts, AGS ensures continuous asset oversight beyond standard business hours. While your team focuses on daytime operations, our team conducts in-depth performance analysis and proactive investigations overnight. This enables your staff to begin each business day with clear, actionable insights instead of unresolved issues, reducing operational delays and supporting faster, more confident decision-making.

How AGS APO Adds Value to Your Operations

• Off-Hours Advanced Analysis with AI Support
While your internal team manages daily activities, AGS leverages AI-driven tools overnight to identify trends, detect anomalies, and prioritize risks. Our analysts perform detailed root-cause analysis across alarms, performance deviations, inverter behaviour, string-level issues, and system anomalies.

• Reduced Lag and Faster Issue Resolution
Events identified during the day are fully analysed overnight using automated diagnostics and expert review. This allows corrective actions to start immediately the next morning, eliminating the typical 12–24-hour response delay.

• Optimized Truck Rolls
AI-assisted diagnostics help differentiate real faults from false alarms. This minimizes unnecessary site visits and ensures technicians are dispatched with accurate information and proper readiness, reducing operational costs and repeat visits.

• Cost Efficiency Without Additional Headcount
AGS APO provides continuous after-hours coverage without the need to expand internal staffing. This results in significant cost savings while maintaining high operational performance.

• 24/7/365 Portfolio Visibility
We deliver uninterrupted monitoring and APO support across nights, weekends, and holidays. AI-driven alerts and performance insights ensure no availability or performance gaps are missed.

Looking forward to discussing how AGS can support {{company_name}}.

Best regards`,
  'Follow-up 1': `Hi {{first_name}},\n\nFollowing up on my previous message regarding {{company_name}}. I wanted to check if you had a chance to review our proposal.\n\nLooking forward to hearing from you.\n\nBest regards`,
  'Follow-up 2': `Hi {{first_name}},\n\nThis is a friendly follow-up regarding {{company_name}}. I understand you may be busy, but I wanted to ensure my message didn't get lost.\n\nPlease let me know if you have any questions.\n\nBest regards`,
  'Follow-up 3': `Hi {{first_name}},\n\nFinal follow-up regarding {{company_name}}. We are here when you are ready to move forward.\n\nFeel free to reach out at your convenience.\n\nBest regards`,
  'Deal Confirmation': `Hi {{first_name}},\n\nThank you for choosing to work with us! I wanted to confirm the details of our agreement with {{company_name}}.\n\nPlease let me know if you have any questions.\n\nBest regards`,
  'Meeting Scheduling Calls': `Hi {{first_name}},\n\nI hope this message finds you well. I wanted to schedule a meeting to discuss the next steps for {{company_name}}.\n\nPlease let me know your availability.\n\nBest regards`,
  'Onboarding Emails': `Hi {{first_name}},\n\nWelcome aboard! We're excited to begin working with {{company_name}}.\n\nAttached you'll find the onboarding documents. Please review and let me know if you have any questions.\n\nBest regards`
};

// Anyone signed in may edit THEIR OWN copies. The restricted list below still
// governs the shared organisation default that new accounts inherit.
const canEditTemplates = (user) => !!user;

const canEditSharedDefault = (user) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const name = (user.name || user.username || '').toLowerCase();
  return role === 'admin' || name.includes('surabhi') || name.includes('janvi') || name.includes('rithika');
};

// Signature image: paste or choose file; store as data URL (actual image, not URL link)
function SignatureImageField({ value, onChange, disabled }) {
  const inputRef = useRef(null);
  const [pasteHint, setPasteHint] = useState(false);
  const valueIsImage = value && (value.startsWith('data:image/') || value.startsWith('http'));

  const setImageFromFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => onChange(e.target.result || '');
    reader.readAsDataURL(file);
  };

  const onPaste = (e) => {
    if (disabled) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        setImageFromFile(items[i].getAsFile());
        return;
      }
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Signature image (optional)</label>
      <p className="text-xs text-gray-500">Paste an image here (Ctrl+V) or choose a file. The image is stored with the template.</p>
      <div
        className="border border-gray-300 rounded-lg p-3 bg-gray-50/50 min-h-[80px] flex flex-wrap items-center gap-3"
        onPaste={onPaste}
        onFocus={() => setPasteHint(true)}
        onBlur={() => setPasteHint(false)}
        tabIndex={0}
      >
        {valueIsImage && (
          <div className="relative inline-block">
            <img src={value} alt="Signature" className="max-h-16 max-w-[200px] object-contain border border-gray-200 rounded" />
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                title="Remove image"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {!disabled && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setImageFromFile(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm hover:bg-gray-50"
            >
              <Image className="w-4 h-4" /> Choose image
            </button>
          </>
        )}
        {pasteHint && !valueIsImage && <span className="text-xs text-gray-500">Paste (Ctrl+V) an image</span>}
      </div>
    </div>
  );
}

// Body editor component (contenteditable with image drag & resize support)
const BodyEditor = ({ stage, body, canEdit, emailStyle, isExpanded, onUpdate, onFocus, setActiveEditorRef, onPaste, onSelectImage }) => {
  const editorRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, isResizing: false, target: null, startX: 0, startY: 0, startWidth: 0, startHeight: 0, startLeft: 0, startTop: 0 });
  const lastSyncedBodyRef = useRef(null); // avoid overwriting DOM when user just pasted (state not yet updated)
  const renderedHtmlRef = useRef(null);   // what we put in the DOM, to spot real edits
  
  useEffect(() => {
    if (!editorRef.current || !isExpanded || !canEdit) return;
    // The same renderer the composer and the outgoing mail use, so the three
    // surfaces cannot drift apart.
    const html = renderEmailBody(body || '', emailStyle);
    // Only sync body -> DOM when body actually changed (prevents overwriting pasted image before state updates)
    if (lastSyncedBodyRef.current !== body) {
      lastSyncedBodyRef.current = body;
      renderedHtmlRef.current = html;
      if (editorRef.current.innerHTML !== html) {
        editorRef.current.innerHTML = html;
      }
    }
    
    // Make all images draggable and resizable (only if not already wrapped with handles)
    const allImages = editorRef.current.querySelectorAll('img');
    const images = Array.from(allImages).filter(img => {
      const parent = img.parentElement;
      // Skip if already in a wrapper with resize handles
      if (parent && parent.classList.contains('image-wrapper') && parent.querySelector('.resize-handle')) {
        return false;
      }
      return true;
    });
    images.forEach(img => {
      // Skip if already wrapped (even without handles - we'll add handles to existing wrappers)
      const parent = img.parentElement;
      if (parent && parent.classList.contains('image-wrapper')) {
        // Already wrapped, just add handles if missing
        if (!parent.querySelector('.resize-handle')) {
          // Add handles to existing wrapper
          const wrapper = parent;
          const handles = ['nw', 'ne', 'se', 'sw'];
          handles.forEach(pos => {
            const handle = document.createElement('div');
            handle.className = `resize-handle resize-handle-${pos}`;
            handle.style.position = 'absolute';
            handle.style.backgroundColor = '#679632';
            handle.style.border = '2px solid white';
            handle.style.width = '12px';
            handle.style.height = '12px';
            handle.style.borderRadius = '2px';
            handle.style.zIndex = '1000';
            handle.style.cursor = getResizeCursor(pos);
            handle.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
            const positions = {
              nw: { top: '-6px', left: '-6px' },
              ne: { top: '-6px', right: '-6px' },
              se: { bottom: '-6px', right: '-6px' },
              sw: { bottom: '-6px', left: '-6px' }
            };
            Object.assign(handle.style, positions[pos]);
            handle.onmousedown = (e) => {
              e.preventDefault();
              e.stopPropagation();
              dragStateRef.current.isResizing = true;
              dragStateRef.current.target = wrapper;
              dragStateRef.current.startX = e.clientX;
              dragStateRef.current.startY = e.clientY;
              dragStateRef.current.startWidth = wrapper.offsetWidth;
              dragStateRef.current.startHeight = wrapper.offsetHeight;
              dragStateRef.current.resizePos = pos;
              wrapper.style.borderColor = '#679632';
            };
            wrapper.appendChild(handle);
          });
          // Add drag handlers to existing wrapper
          wrapper.onmousedown = (e) => {

            if (e.target.classList.contains('resize-handle')) return;
            // Select the image — do NOT preventDefault or stopPropagation here.
            // Swallowing the event stopped the click ever reaching the editor, so
            // the image toolbar (size, align, Make a row) never appeared at all.
            const img = wrapper.querySelector('img');
            if (img) onSelectImage?.(img, e.ctrlKey || e.metaKey);
            wrapper.style.borderColor = '#679632';
          };
          wrapper.onmouseenter = () => {
            if (!dragStateRef.current.isDragging && !dragStateRef.current.isResizing) {
              wrapper.style.borderColor = '#679632';
            }
          };
          wrapper.onmouseleave = () => {
            if (!dragStateRef.current.isDragging && !dragStateRef.current.isResizing) {
              wrapper.style.borderColor = 'transparent';
            }
          };
        }
        return; // Skip wrapping, already wrapped
      }
      
      img.contentEditable = 'false';
      img.draggable = false;
      
      // Wrap image in a container for resize handles
      const wrapper = document.createElement('div');
      wrapper.className = 'image-wrapper';
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';
      wrapper.style.verticalAlign = 'top';
      wrapper.style.margin = '4px';
      wrapper.style.border = '2px dashed transparent';
      wrapper.style.transition = 'border-color 0.2s';
      
      img.parentNode.insertBefore(wrapper, img);
      wrapper.appendChild(img);
      
      // Ensure image has proper styling
      // Do NOT restyle the image. Forcing display:block put every image on its
      // own line, which is what dropped the Instagram icon below LinkedIn in a
      // signature that had them side by side. Images keep whatever layout they
      // were pasted with; only the wrapper carries the editing affordances.
      img.style.cursor = 'default';
      
      // Create resize handles (corners only for simplicity - 4 handles)
      const handles = ['nw', 'ne', 'se', 'sw'];
      handles.forEach(pos => {
        const handle = document.createElement('div');
        handle.className = `resize-handle resize-handle-${pos}`;
        handle.style.position = 'absolute';
        handle.style.backgroundColor = '#679632';
        handle.style.border = '2px solid white';
        handle.style.width = '12px';
        handle.style.height = '12px';
        handle.style.borderRadius = '2px';
        handle.style.zIndex = '1000';
        handle.style.cursor = getResizeCursor(pos);
        handle.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
        
        // Position handle at corners
        const positions = {
          nw: { top: '-6px', left: '-6px' },
          ne: { top: '-6px', right: '-6px' },
          se: { bottom: '-6px', right: '-6px' },
          sw: { bottom: '-6px', left: '-6px' }
        };
        Object.assign(handle.style, positions[pos]);
        
        handle.onmousedown = (e) => {
          e.preventDefault();
          e.stopPropagation();
          dragStateRef.current.isResizing = true;
          dragStateRef.current.target = wrapper;
          dragStateRef.current.startX = e.clientX;
          dragStateRef.current.startY = e.clientY;
          dragStateRef.current.startWidth = wrapper.offsetWidth;
          dragStateRef.current.startHeight = wrapper.offsetHeight;
          dragStateRef.current.resizePos = pos;
          wrapper.style.borderColor = '#679632';
        };
        
        wrapper.appendChild(handle);
      });
      
      // Make wrapper draggable (click and drag on image)
      wrapper.onmousedown = (e) => {

        if (e.target.classList.contains('resize-handle')) return;
        // Select the image — do NOT preventDefault or stopPropagation here.
        // Swallowing the event stopped the click ever reaching the editor, so
        // the image toolbar (size, align, Make a row) never appeared at all.
        const img = wrapper.querySelector('img');
        if (img) onSelectImage?.(img, e.ctrlKey || e.metaKey);
        wrapper.style.borderColor = '#679632';
      };
      
      // Show handles on hover
      wrapper.onmouseenter = () => {
        if (!dragStateRef.current.isDragging && !dragStateRef.current.isResizing) {
          wrapper.style.borderColor = '#679632';
        }
      };
      wrapper.onmouseleave = () => {
        if (!dragStateRef.current.isDragging && !dragStateRef.current.isResizing) {
          wrapper.style.borderColor = 'transparent';
        }
      };
    });
    
    // After wrapping new images, persist wrapper HTML to state so next effect doesn't overwrite
    const hadNewWrappers = images.length > 0;
    if (hadNewWrappers && editorRef.current) {
      onUpdate(editorRef.current.innerHTML);
    }
    
    // Global mouse move/up handlers
    const handleMouseMove = (e) => {
      if (!dragStateRef.current.isDragging && !dragStateRef.current.isResizing) return;
      
      if (dragStateRef.current.isResizing && dragStateRef.current.target) {
        const wrapper = dragStateRef.current.target;
        const deltaX = e.clientX - dragStateRef.current.startX;
        const deltaY = e.clientY - dragStateRef.current.startY;
        const pos = dragStateRef.current.resizePos;
        let newWidth = dragStateRef.current.startWidth;
        let newHeight = dragStateRef.current.startHeight;
        
        // Calculate new dimensions based on corner
        if (pos === 'se') {
          newWidth += deltaX;
          newHeight += deltaY;
        } else if (pos === 'sw') {
          newWidth -= deltaX;
          newHeight += deltaY;
        } else if (pos === 'ne') {
          newWidth += deltaX;
          newHeight -= deltaY;
        } else if (pos === 'nw') {
          newWidth -= deltaX;
          newHeight -= deltaY;
        }
        
        // Maintain aspect ratio and minimum size
        if (newWidth > 50 && newHeight > 50) {
          const img = wrapper.querySelector('img');
          if (img && img.naturalWidth && img.naturalHeight) {
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            // Use width as primary, adjust height
            const finalHeight = newWidth / aspectRatio;
            wrapper.style.width = newWidth + 'px';
            wrapper.style.height = finalHeight + 'px';
            // Pixel sizes on the image itself, and on the width/height attributes
            // Outlook actually honours — `width:100%` only worked while the
            // wrapper existed, so it exploded the moment the mail was sent.
            img.style.width = Math.round(newWidth) + 'px';
            img.style.height = Math.round(finalHeight) + 'px';
            img.setAttribute('width', Math.round(newWidth));
            img.setAttribute('height', Math.round(finalHeight));
          } else {
            wrapper.style.width = newWidth + 'px';
            wrapper.style.height = newHeight + 'px';
          }
        }
      } else if (dragStateRef.current.isDragging && dragStateRef.current.target) {
        const wrapper = dragStateRef.current.target;
        const deltaX = e.clientX - dragStateRef.current.startX;
        const deltaY = e.clientY - dragStateRef.current.startY;
        const editorRect = editorRef.current.getBoundingClientRect();
        const newLeft = dragStateRef.current.startLeft + deltaX;
        const newTop = dragStateRef.current.startTop + deltaY;
        
        // Keep within editor bounds
        if (newLeft >= 0 && newTop >= 0 && 
            newLeft + wrapper.offsetWidth <= editorRect.width &&
            newTop + wrapper.offsetHeight <= editorRect.height) {
          // Deliberately NOT applied. Dragging used to set position:absolute with
          // left/top on the wrapper. Mail clients drop absolute positioning, so
          // every dragged image landed somewhere else in the recipient's inbox
          // (and in our own narrower composer). Images stay in the text flow;
          // resizing still works via the corner handles.
        }
      }
    };
    
    const handleMouseUp = () => {
      if (dragStateRef.current.target) {
        dragStateRef.current.target.style.opacity = '1';
        if (!dragStateRef.current.isResizing) {
          dragStateRef.current.target.style.borderColor = 'transparent';
        }
      }
      if (dragStateRef.current.isDragging || dragStateRef.current.isResizing) {
        if (editorRef.current) onUpdate(editorRef.current.innerHTML);
      }
      dragStateRef.current.isDragging = false;
      dragStateRef.current.isResizing = false;
      dragStateRef.current.target = null;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [body, isExpanded, onUpdate, canEdit]);
  
  useEffect(() => {
    if (isExpanded && editorRef.current) {
      setActiveEditorRef(editorRef.current);
    }
  }, [isExpanded, setActiveEditorRef]);
  
  return (
    <div
      ref={editorRef}
      contentEditable={canEdit}
      suppressContentEditableWarning
      onFocus={() => {
        setActiveEditorRef(editorRef.current);
        onFocus?.();
      }}
      onBlur={(e) => {
        // Saving on every blur would rewrite an untouched template with its
        // rendered form; only a real change is worth storing.
        if (!canEdit) return;
        const html = e.currentTarget.innerHTML;
        if (html !== renderedHtmlRef.current) onUpdate(html);
      }}
      onPaste={onPaste}
      onClick={(e) => onSelectImage?.(
        e.target && e.target.tagName === 'IMG' ? e.target : null,
        e.ctrlKey || e.metaKey
      )}
      className={`email-body w-full border ${canEdit ? 'border-t-0' : 'border-gray-300'} border-gray-300 rounded-b-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:outline-none min-h-[200px] ${!canEdit ? 'bg-gray-100' : ''}`}
      /* Pasted HTML carries its own fonts, sizes and colours — imposing ours
         here made the Templates page disagree with the composer and with the
         delivered mail. Only plain typed text gets the style defaults. */
      style={(typeof body === 'string' && body.includes('<'))
        ? { position: 'relative' }
        : {
            fontFamily: emailStyle.fontFamily,
            fontSize: emailStyle.fontSize,
            lineHeight: emailStyle.lineHeight,
            color: emailStyle.textColor,
            position: 'relative'
          }}
    />
  );
};

const getResizeCursor = (pos) => {
  const cursors = {
    nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize',
    e: 'e-resize', se: 'se-resize', s: 's-resize',
    sw: 'sw-resize', w: 'w-resize'
  };
  return cursors[pos] || 'move';
};

function CustomTemplateEditForm({ template, saving, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: template.name || '',
    subject: template.subject || '',
    body: template.body || '',
    header: template.header || '',
    signature_image_url: template.signature_image_url || ''
  });
  useEffect(() => {
    setForm({
      name: template.name || '',
      subject: template.subject || '',
      body: template.body || '',
      header: template.header || '',
      signature_image_url: template.signature_image_url || ''
    });
  }, [template.id]);
  const handleSave = () => {
    const name = (form.name || '').trim();
    if (!name) return;
    onSave({ name, subject: form.subject, body: form.body, header: form.header, signature_image_url: form.signature_image_url });
  };
  return (
    <div className="p-4 bg-white space-y-3 border-t border-gray-200">
      <input
        placeholder="Template name (required)"
        value={form.name}
        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      <input
        placeholder="Subject"
        value={form.subject}
        onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      <RichEmailEditor
        value={form.body}
        onChange={(html) => setForm(prev => ({ ...prev, body: html }))}
        minHeight={200}
        placeholder="Body — paste anything, formatting and images are kept. Use {{first_name}} and {{company_name}}."
      />
      <input
        placeholder="Header (optional)"
        value={form.header}
        onChange={(e) => setForm(prev => ({ ...prev, header: e.target.value }))}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      <SignatureImageField
        value={form.signature_image_url}
        onChange={(v) => setForm(prev => ({ ...prev, signature_image_url: v || '' }))}
        disabled={saving}
      />
      <div className="flex gap-2">
        <button type="button" onClick={handleSave} disabled={saving} className="px-3 py-1.5 bg-[#679632] text-white rounded text-sm disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 border border-gray-300 rounded text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedStage, setExpandedStage] = useState(null); // which category is open to show subject/body
  const [emailStyles, setEmailStyles] = useState({}); // stage -> { fontFamily, fontSize, lineHeight, textColor }
  const [customTemplates, setCustomTemplates] = useState([]); // { id, name, subject, body, header, signature_image_url }[]
  const [expandedCustomId, setExpandedCustomId] = useState(null); // which custom template is expanded
  const [addingCustom, setAddingCustom] = useState(false); // show add form
  const [customForm, setCustomForm] = useState({ name: '', subject: '', body: '', header: '', signature_image_url: '' });
  const activeEditorRef = useRef(null); // Currently focused contenteditable div
  const activeStageRef = useRef(null); // Stage of active editor
  const fileInputRef = useRef(null); // For image attachment
  const savedSelectionRef = useRef(null); // For Link: save selection before prompt

  const canEdit = canEditTemplates(user);
  // ---- Arranging images (signatures, logos) -------------------------------
  // Click an image in the body and the toolbar can size and place it. Alignment
  // and side-by-side are expressed the way mail clients understand: a line's
  // text-align, and images sharing one line. No floats, no flexbox.
  // 'mine' = this account's own templates; 'default' = the shared set new
  // accounts inherit (restricted).
  const [templateScope, setTemplateScope] = useState('mine');
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedImg, setSelectedImg] = useState(null);
  const [selectedImgWidth, setSelectedImgWidth] = useState('');
  const [selectedImgHref, setSelectedImgHref] = useState('');
  // Ctrl/Cmd-click builds up a set of images to line up together.
  const [pickedImgs, setPickedImgs] = useState([]);
  const selectedImgStageRef = useRef(null);

  // Clear every outline from the DOM rather than from remembered state: this
  // runs from a DOM event handler installed inside an effect, so any captured
  // array is a render behind and would leave stale green rings behind.
  const clearImageOutlines = (keep) => {
    const el = activeEditorRef.current;
    if (!el) return;
    el.querySelectorAll('img').forEach(i => { if (i !== keep) i.style.outline = ''; });
  };

  const widthOf = (img) => String(
    parseInt(img.getAttribute('width'), 10) || parseInt(img.style.width, 10)
    || img.clientWidth || img.naturalWidth || ''
  );

  const selectTemplateImage = (img, stage, additive = false) => {
    selectedImgStageRef.current = stage ?? selectedImgStageRef.current;
    if (additive && img) {
      img.style.outline = '2px solid #679632';
      setPickedImgs(prev => (prev.includes(img) ? prev : [...prev, img]));
      setSelectedImg(img);
      setSelectedImgWidth(widthOf(img));
      setSelectedImgHref(imageLinkHref(img));
      return;
    }
    clearImageOutlines(img);
    setPickedImgs([]);
    if (!img) { setSelectedImg(null); setSelectedImgWidth(''); setSelectedImgHref(''); return; }
    img.style.outline = '2px solid #679632';
    setSelectedImg(img);
    setSelectedImgWidth(widthOf(img));
    setSelectedImgHref(imageLinkHref(img));
  };

  // Saving re-renders the editor from the stored body, which REPLACES every
  // node in it — so the image we were holding becomes a detached element and
  // every further press on the toolbar silently does nothing to the visible
  // body. Re-find the same image by its position once the new DOM is in place.
  const commitImageChange = (keepImage = true) => {
    const el = activeEditorRef.current;
    const stage = selectedImgStageRef.current || activeStageRef.current;
    if (!el || !stage) return;
    const images = Array.from(el.querySelectorAll('img'));
    const position = keepImage && selectedImg ? images.indexOf(selectedImg) : -1;
    updateStage(stage, 'body', el.innerHTML);
    if (position < 0) { selectTemplateImage(null); return; }
    setTimeout(() => {
      const live = activeEditorRef.current;
      if (!live) return;
      selectTemplateImage(live.querySelectorAll('img')[position] || null);
    }, 0);
  };

  const arrangeSelected = (action) => {
    const img = selectedImg;
    const root = activeEditorRef.current;
    if (!img || !root) return;
    if (action === 'left' || action === 'center' || action === 'right') alignImage(img, action, root);
    if (action === 'join') joinPreviousLine(img, root);
    if (action === 'own') moveToOwnLine(img, root);
    if (action === 'moveLeft' || action === 'moveRight') {
      moveImage(img, action === 'moveLeft' ? 'left' : 'right', root);
    }
    if (action === 'row') {
      const chosen = pickedImgs.length >= 2 ? pickedImgs : imagesInSelection(root);
      const count = chosen.length >= 2
        ? makeRowFromImageList(chosen, root)
        : makeRowFromImage(img, root);
      chosen.forEach(i => { i.style.outline = ''; });
      setPickedImgs([]);
      if (count < 2) {
        window.alert('Pick the images first: drag across them, or Ctrl/Cmd-click each one — then press Make a row. Clicking a single image lines it up with the image-only lines below it.');
      }
    }
    commitImageChange();
  };

  // Point a picture at a URL. execCommand('createLink') cannot: it needs
  // selected TEXT, which is why the LinkedIn and Instagram badges in a
  // signature could never be made clickable.
  const linkSelectedImage = () => {
    if (!selectedImg) return;
    const current = imageLinkHref(selectedImg);
    const url = window.prompt('Link this image to (leave empty to remove the link):', current || 'https://');
    if (url === null) return;
    const cleaned = url.trim() === 'https://' ? '' : url.trim();
    linkImage(selectedImg, cleaned);
    setSelectedImgHref(imageLinkHref(selectedImg));
    commitImageChange();
  };

  const resizeSelected = (width) => {
    if (!selectedImg) return;
    const applied = setImageWidth(selectedImg, width);
    if (applied) setSelectedImgWidth(String(applied));
    commitImageChange();
  };

  // Bodies are not loaded up front (one of them is 1.7 MB), so opening a stage
  // fetches it once and keeps it.
  const [loadingBody, setLoadingBody] = useState(null);
  const loadedBodiesRef = useRef(new Set());

  const ensureBodyLoaded = async (stage) => {
    if (!stage || loadedBodiesRef.current.has(stage)) return;
    loadedBodiesRef.current.add(stage);
    try {
      setLoadingBody(stage);
      const res = await dashboardAPI.getEmailTemplateBody(stage);
      if (res?.success && res.template) {
        setTemplates(prev => ({
          ...prev,
          [stage]: {
            ...(prev[stage] || {}),
            ...res.template,
            // Nothing saved anywhere for this stage → start from the built-in text.
            body: stripEditorChrome(res.template.body || '') || (DEFAULT_BODIES[stage] || ''),
            bodyLoaded: true
          }
        }));
      } else {
        loadedBodiesRef.current.delete(stage); // let the next click try again
      }
    } catch (e) {
      loadedBodiesRef.current.delete(stage); // let the next click try again
      console.error('Could not load that template body:', e.message);
    } finally {
      setLoadingBody(null);
    }
  };

  const toggleStage = (stage) => {
    setExpandedStage(prev => {
      const next = prev === stage ? null : stage;
      if (next) ensureBodyLoaded(next);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dashboardAPI.getEmailTemplates({ meta: true, scope: templateScope === 'default' ? 'default' : undefined });
        if (cancelled) return;
        if (res.success && res.templates) {
          const filled = {};
          STAGE_OPTIONS.forEach(s => {
            const t = res.templates[s] || {};
            const savedSubject = (t.subject != null && String(t.subject).trim() !== '') ? String(t.subject) : '';
            // The list comes without bodies (meta=1). Mark each one as not
            // loaded: the body is fetched when the stage is opened, and Save
            // skips any stage whose body never arrived. Filling in the built-in
            // default here used to make Save write that default over every
            // stage you had not opened — "my follow-up template keeps going
            // back to the default".
            filled[s] = {
              subject: savedSubject || DEFAULT_SUBJECT,
              body: '',
              bodyLoaded: false,
              header: t.header ?? '',
              signature_image_url: t.signature_image_url ?? ''
            };
          });
          setTemplates(filled);
          // Initialize email styles for all stages
          const styles = {};
          STAGE_OPTIONS.forEach(s => {
            styles[s] = { ...DEFAULT_EMAIL_STYLE };
          });
          setEmailStyles(styles);
        } else {
          const withDefaults = {};
          STAGE_OPTIONS.forEach(s => {
            withDefaults[s] = {
              subject: DEFAULT_SUBJECT,
              body: '',
              bodyLoaded: false,
              header: '',
              signature_image_url: ''
            };
          });
          setTemplates(withDefaults);
        }
      } catch (e) {
        if (!cancelled) {
          const withDefaults = {};
          STAGE_OPTIONS.forEach(s => {
            withDefaults[s] = {
              subject: DEFAULT_SUBJECT,
              body: '',
              bodyLoaded: false,
              header: '',
              signature_image_url: ''
            };
          });
          setTemplates(withDefaults);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, templateScope]);

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    dashboardAPI.getCustomEmailTemplates(user.id).then(res => {
      if (!cancelled && res?.success && Array.isArray(res.data)) setCustomTemplates(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  /**
   * Bullets and text colour, the same as the Send Emails toolbar.
   *
   * Two things to get right, and neither is obvious. The SELECTION is gone the
   * moment a colour picker takes focus, so it is remembered on the way in and
   * restored before the command runs. And the edit has to be written back to
   * the template being edited — the editor is a contenteditable, so a change
   * that is not saved to state is lost on the next render.
   */
  const rememberSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (activeEditorRef.current && activeEditorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const runInActiveEditor = (command, value = null, { restore = false } = {}) => {
    const el = activeEditorRef.current;
    if (!el) return;
    el.focus();
    if (restore && savedSelectionRef.current) {
      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(savedSelectionRef.current);
      } catch (_) { /* the editor was re-rendered; use the caret as it stands */ }
    }
    // CSS rather than <font>, so the colour is an inline style every mail
    // client and our own send pipeline understand.
    try { document.execCommand('styleWithCSS', false, true); } catch (_) {}
    document.execCommand(command, false, value);
    try { document.execCommand('styleWithCSS', false, false); } catch (_) {}
    savedSelectionRef.current = null;
    if (activeStageRef.current) updateStage(activeStageRef.current, 'body', el.innerHTML);
  };

  const updateStage = (stage, field, value) => {
    if (!canEdit) return;
    setTemplates(prev => ({
      ...prev,
      // Typing in the body makes it real content, so Save will keep it.
      [stage]: { ...(prev[stage] || { subject: '', body: '', header: '', signature_image_url: '' }), [field]: value, ...(field === 'body' && value ? { bodyLoaded: true } : {}) }
    }));
  };

  const updateEmailStyle = (stage, field, value) => {
    if (!canEdit) return;
    setEmailStyles(prev => ({
      ...prev,
      [stage]: { ...(prev[stage] || { ...DEFAULT_EMAIL_STYLE }), [field]: value }
    }));
  };

  const createCustomTemplate = async () => {
    const name = (customForm.name || '').trim();
    if (!name) {
      setMessage({ type: 'error', text: 'Template name is required.' });
      return;
    }
    if (!user?.id) {
      setMessage({ type: 'error', text: 'You must be logged in to create templates.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await dashboardAPI.createCustomEmailTemplate(user.id, {
        name,
        subject: customForm.subject || '',
        body: await slimEmailBody(customForm.body || ''),
        header: customForm.header || '',
        signature_image_url: customForm.signature_image_url || ''
      });
      if (res?.success && res?.data) {
        setCustomTemplates(prev => [...prev, res.data]);
        setAddingCustom(false);
        setCustomForm({ name: '', subject: '', body: '', header: '', signature_image_url: '' });
        setMessage({ type: 'success', text: 'Template created.' });
      } else {
        setMessage({ type: 'error', text: res?.message || 'Failed to create template.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'Failed to create template.' });
    } finally {
      setSaving(false);
    }
  };

  const updateCustomTemplate = async (id, payload) => {
    if (!user?.id) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Same as everywhere else a body is written: settle the pictures first so
      // the request cannot be too large. See utils/emailImages.
      const slimmed = typeof payload?.body === 'string'
        ? { ...payload, body: await slimEmailBody(payload.body) }
        : payload;
      const res = await dashboardAPI.updateCustomEmailTemplate(id, user.id, slimmed);
      if (res?.success && res?.data) {
        setCustomTemplates(prev => prev.map(c => c.id === id ? { ...c, ...res.data } : c));
        setExpandedCustomId(null);
        setMessage({ type: 'success', text: 'Template updated.' });
      } else {
        setMessage({ type: 'error', text: res?.message || 'Failed to update template.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'Failed to update template.' });
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomTemplate = async (id) => {
    if (!window.confirm('Delete this template? It will no longer appear in Send Emails.')) return;
    if (!user?.id) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await dashboardAPI.deleteCustomEmailTemplate(id, user.id);
      if (res?.success) {
        setCustomTemplates(prev => prev.filter(c => c.id !== id));
        setExpandedCustomId(prev => prev === id ? null : prev);
        setMessage({ type: 'success', text: 'Template deleted.' });
      } else {
        setMessage({ type: 'error', text: res?.message || 'Failed to delete template.' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e?.message || 'Failed to delete template.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle image paste in contenteditable (paste target = the focused editor)
  const handlePaste = (e, stage) => {
    if (!canEdit) return;
    // Capture the editor at paste time (the event target is reliable; a ref may
    // have moved on by the time an async reader callback runs).
    const editorEl = (e.target && e.target.getAttribute?.('contenteditable') === 'true')
      ? e.target
      : activeEditorRef.current;
    if (!editorEl) return;
    // Images become data URLs, formatted content is cleaned of Word/Outlook page
    // furniture but keeps every bit of real formatting.
    handleRichPasteWithImages(e, editorEl, (html) => updateStage(stage, 'body', html));
  };

  const handleSave = async () => {
    if (!canEdit || !user?.id) {
      setMessage({ type: 'error', text: 'Sign in to save your templates.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Only send stages whose body is actually in hand. A stage that was never
      // opened has no body loaded, and writing '' back would erase it.
      // Pictures are settled before the save, not during it: a signature pasted
      // straight out of Outlook can carry megabytes of base64, and the request
      // body has a hard 4.5 MB ceiling. slimEmailBody re-encodes what it can and
      // parks the rest in storage, so this always fits. See utils/emailImages.
      const list = await Promise.all(STAGE_OPTIONS.map(async stage => {
        const t = templates[stage] || {};
        const bodyKnown = t.bodyLoaded !== false && typeof t.body === 'string';
        // Drag/resize handles are editor UI, not content — never store them.
        const body = bodyKnown ? await slimEmailBody(stripEditorChrome(t.body || '')) : undefined;
        return {
          stage,
          subject: t.subject || '',
          ...(bodyKnown ? { body } : {}),
          header: t.header || '',
          signature_image_url: t.signature_image_url || ''
        };
      }));
      const filtered = list.filter(t => t.body !== undefined || t.subject || t.header || t.signature_image_url);
      const res = await dashboardAPI.updateEmailTemplates(user.id, filtered, templateScope === 'default' ? 'default' : undefined);
      if (res.success) {
        setMessage({ type: 'success', text: 'Templates saved. They will be used as defaults in Send Emails.' });
      } else {
        setMessage({ type: 'error', text: res.error || 'Failed to save' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || e.message || 'Failed to save' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 4000);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    const onAppSave = () => { if (canEdit) handleSaveRef.current?.(); };
    window.addEventListener('app-save', onAppSave);
    return () => window.removeEventListener('app-save', onAppSave);
  }, [canEdit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-gray-500">Loading templates…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {templateScope === 'default' ? 'Shared default templates' : 'My email templates'}
          </h1>
          <p className="text-xs text-gray-500">
            {templateScope === 'default'
              ? 'What every account starts from until they change their own.'
              : 'Your own copies — the wording is shared, the signature is yours.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canEditSharedDefault(user) && (
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={templateScope === 'default'}
                onChange={(e) => {
                  setTemplateScope(e.target.checked ? 'default' : 'mine');
                  // Different set entirely — drop what is loaded and start again.
                  loadedBodiesRef.current = new Set();
                  setExpandedStage(null);
                  setReloadKey(k => k + 1);
                }}
                className="rounded border-gray-300 text-[#679632]"
              />
              Edit the shared defaults
            </label>
          )}
          {!canEdit && (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Read-only. Create your own below.
            </span>
          )}
          {canEdit && (
            <button type="button" onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 bg-[#679632] text-white rounded-lg hover:bg-[#5a8229] disabled:opacity-50 text-sm font-medium">
              {saving ? 'Saving...' : 'Save All'}
            </button>
          )}
        </div>
      </div>

      {message.text && (
        <p className={`text-sm rounded-lg px-3 py-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </p>
      )}

      {/* Stage Templates */}
      <div className="space-y-2">
        {STAGE_OPTIONS.map((stage) => {
          const Icon = STAGE_ICONS[stage] || FileText;
          const data = templates[stage] || { subject: '', body: '', header: '', signature_image_url: '' };
          const isExpanded = expandedStage === stage;
          const isInherited = templateScope !== 'default' && data.personal === false;
          return (
            <div key={stage} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button type="button" onClick={() => toggleStage(stage)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-800 font-medium hover:bg-gray-50 transition-colors">
                {isExpanded ? <ChevronDown className="w-4 h-4 text-[#679632] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#679632] shrink-0" />}
                <Icon className="w-4 h-4 text-[#679632] shrink-0" />
                <span className="text-sm">{stage}</span>
                {isInherited ? (
                  <span
                    title="You have not changed this one yet, so you are seeing the shared version. Save it and it becomes your own copy."
                    className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200"
                  >
                    shared
                  </span>
                ) : (
                  templateScope !== 'default' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#eaf3e0] text-[#4a6f22]">yours</span>
                  )
                )}
                {data.subject && <span className="ml-auto text-xs text-gray-400 truncate max-w-[300px]">{data.subject}</span>}
              </button>
              {isExpanded && (
                <div className="border-t border-gray-200 p-4 bg-gray-50/50 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={data.subject}
                      onChange={(e) => updateStage(stage, 'subject', e.target.value)}
                      placeholder="e.g. Follow-up from AGS Sales"
                      disabled={!canEdit}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Header (e.g. greeting, shown above body)</label>
                    <input
                      type="text"
                      value={data.header}
                      onChange={(e) => updateStage(stage, 'header', e.target.value)}
                      placeholder="e.g. Hi {{first_name}},"
                      disabled={!canEdit}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                  {canEdit && (
                    <div>
                      <SignatureImageField
                        value={data.signature_image_url || ''}
                        onChange={(v) => updateStage(stage, 'signature_image_url', v || '')}
                        disabled={!canEdit}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                    <p className="text-xs text-gray-500 mb-1">Use {'{{first_name}}'} and {'{{company_name}}'} for personalization. Paste images (e.g. signature PNG) and click to resize.</p>
                    
                    {/* Formatting Toolbar */}
                    {canEdit && (
                      <div className="flex flex-wrap items-center gap-3 py-2 px-3 mb-0 bg-white border border-b-0 border-gray-300 rounded-t-lg">
                        <span className="text-xs font-medium text-gray-600 shrink-0">Style:</span>
                        <select
                          value={(emailStyles[stage] || DEFAULT_EMAIL_STYLE).fontFamily}
                          onChange={(e) => updateEmailStyle(stage, 'fontFamily', e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          title="Font"
                        >
                          {EMAIL_FONT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <select
                          value={(emailStyles[stage] || DEFAULT_EMAIL_STYLE).fontSize}
                          onChange={(e) => updateEmailStyle(stage, 'fontSize', e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          title="Size"
                        >
                          {EMAIL_FONT_SIZE_OPTIONS.map((px) => (
                            <option key={px} value={px}>{px}</option>
                          ))}
                        </select>
                        <select
                          value={(emailStyles[stage] || DEFAULT_EMAIL_STYLE).lineHeight}
                          onChange={(e) => updateEmailStyle(stage, 'lineHeight', e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#679632] focus:border-transparent"
                          title="Line height"
                        >
                          {EMAIL_LINE_HEIGHT_OPTIONS.map((lh) => (
                            <option key={lh} value={lh}>{lh}</option>
                          ))}
                        </select>
                        <span className="text-gray-300 mx-1">|</span>
                        <span className="text-xs font-medium text-gray-600 shrink-0">Align:</span>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align left" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyLeft', false, null); }}>L</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align center" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyCenter', false, null); }}>C</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Align right" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyRight', false, null); }}>R</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 font-medium" title="Justify" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('justifyFull', false, null); }}>J</button>
                        <span className="text-gray-300 mx-1">|</span>
                        <span className="text-xs font-medium text-gray-600 shrink-0">Format:</span>
                        <button type="button" className="px-2 py-1.5 text-sm font-bold border border-gray-300 bg-white rounded hover:bg-gray-50" title="Bold" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('bold', false, null); }}>B</button>
                        <button type="button" className="px-2 py-1.5 text-sm italic border border-gray-300 bg-white rounded hover:bg-gray-50" title="Italic" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('italic', false, null); }}>I</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 underline" title="Underline" onMouseDown={(e) => { e.preventDefault(); activeEditorRef.current?.focus(); document.execCommand('underline', false, null); }}>U</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Bullet list — turns the line (or the selected lines) into bullet points, and off again" onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('insertUnorderedList'); }}>• List</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Numbered list" onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('insertOrderedList'); }}>1. List</button>
                        <span className="inline-flex items-center gap-1 pl-2 ml-1 border-l border-gray-300">
                          <span className="text-xs font-medium text-gray-600">Colour:</span>
                          {[['#000000', 'Black'], ['#679632', 'AGS green'], ['#c0392b', 'Red'], ['#2563eb', 'Blue'], ['#6b7280', 'Grey']].map(([hex, label]) => (
                            <button
                              key={hex}
                              type="button"
                              title={`${label} — select text first`}
                              onMouseDown={(e) => { e.preventDefault(); runInActiveEditor('foreColor', hex); }}
                              className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform"
                              style={{ backgroundColor: hex }}
                            />
                          ))}
                          <input
                            type="color"
                            title="Any other colour — select the text first, then pick"
                            defaultValue="#679632"
                            onMouseDown={rememberSelection}
                            onChange={(e) => runInActiveEditor('foreColor', e.target.value, { restore: true })}
                            className="w-7 h-7 p-0 border border-gray-300 rounded bg-white cursor-pointer"
                          />
                        </span>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 text-blue-600" title="Insert link (select text first)" onMouseDown={(e) => { e.preventDefault(); const sel = window.getSelection(); const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null; if (range && activeEditorRef.current && activeEditorRef.current.contains(range.commonAncestorContainer)) savedSelectionRef.current = range; const url = window.prompt('Enter URL:'); const el = activeEditorRef.current; if (url && el) { el.focus(); if (savedSelectionRef.current) { try { const s = window.getSelection(); s.removeAllRanges(); s.addRange(savedSelectionRef.current); } catch (_) {} savedSelectionRef.current = null; } document.execCommand('createLink', false, url.trim().startsWith('http') ? url.trim() : 'https://' + url.trim()); if (activeStageRef.current) updateStage(activeStageRef.current, 'body', el.innerHTML); } }}>Link</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Insert a table" onMouseDown={(e) => { e.preventDefault(); const el = activeEditorRef.current; if (!el || !activeStageRef.current) return; const spec = window.prompt('Table size as rows x columns:', '3x3'); if (!spec) return; const [r, c] = spec.toLowerCase().split(/[x×,\s]+/).map(n => parseInt(n, 10)); if (!r || !c) return; el.focus(); document.execCommand('insertHTML', false, buildTableHtml(Math.min(r, 20), Math.min(c, 10))); updateStage(activeStageRef.current, 'body', el.innerHTML); }}>Table</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Turn the lines of the table around the cursor on or off" onMouseDown={(e) => { e.preventDefault(); const el = activeEditorRef.current; if (!el || !activeStageRef.current) return; const table = tableFromSelection(el); if (!table) { window.alert('Click inside the table first.'); return; } setTableBorders(table, !tableHasBorders(table)); updateStage(activeStageRef.current, 'body', el.innerHTML); }}>Borders</button>
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-600" title="Tidy up Word/Outlook paste — removes stray boxes, page widths and blank spacer lines, keeps your formatting" onMouseDown={(e) => { e.preventDefault(); const el = activeEditorRef.current; if (!el || !activeStageRef.current) return; el.innerHTML = sanitizePastedHtml(el.innerHTML); updateStage(activeStageRef.current, 'body', el.innerHTML); }}>Tidy</button>
                        {selectedImg && (
                          <span className="flex items-center gap-1 pl-2 ml-1 border-l border-gray-300">
                            <span className="text-xs text-gray-600">
                              {pickedImgs.length >= 2 ? `${pickedImgs.length} picked` : 'Image'}
                            </span>
                            <input
                              type="number" min="8" max="2000" value={selectedImgWidth}
                              onChange={(e) => setSelectedImgWidth(e.target.value)}
                              onBlur={() => resizeSelected(selectedImgWidth)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resizeSelected(selectedImgWidth); } }}
                              className="w-16 border border-gray-300 rounded px-1 py-0.5 text-xs"
                              title="Width in pixels — height follows"
                            />
                            <span className="text-xs text-gray-500">px</span>
                            <button type="button" title={selectedImgHref ? `Linked to ${selectedImgHref} — click to change or remove` : 'Link this image to a URL (LinkedIn, Instagram, your site)'} onMouseDown={(e) => { e.preventDefault(); linkSelectedImage(); }} className={`px-2 py-1.5 text-xs rounded font-medium border ${selectedImgHref ? 'border-[#679632] text-[#679632] bg-[#f1f7ec]' : 'border-blue-300 text-blue-600 bg-white hover:bg-blue-50'}`}>{selectedImgHref ? '🔗 Linked' : '🔗 Link'}</button>
                            <button type="button" title="Move this image one place left" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('moveLeft'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">◀</button>
                            <button type="button" title="Move this image one place right" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('moveRight'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">▶</button>
                            <button type="button" title="Line this image up with the image-only lines below it, as one row" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('row'); }} className="px-2 py-1.5 text-xs border border-[#679632] text-[#679632] bg-white rounded hover:bg-[#f1f7ec] font-medium">Make a row</button>
                            <button type="button" title="Put this image on the line above, beside the previous one" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('join'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">Same line</button>
                            <button type="button" title="Give this image its own line" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('own'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">Own line</button>
                            <button type="button" title="Align this line left" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('left'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇤</button>
                            <button type="button" title="Centre this line" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('center'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇔</button>
                            <button type="button" title="Align this line right" onMouseDown={(e) => { e.preventDefault(); arrangeSelected('right'); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">⇥</button>
                            <button type="button" title="Deselect" onMouseDown={(e) => { e.preventDefault(); selectTemplateImage(null); }} className="px-2 py-1.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50 text-gray-500">✕</button>
                          </span>
                        )}
                        <button type="button" className="px-2 py-1.5 text-sm border border-gray-300 bg-white rounded hover:bg-gray-50" title="Attach image (or paste image directly)" onMouseDown={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}>Attach</button>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const files = e.target.files ? Array.from(e.target.files) : []; if (!files.length || !activeStageRef.current) { e.target.value = ''; return; } const file = files[0]; const reader = new FileReader(); reader.onload = (event) => { const dataUrl = event.target.result; const img = document.createElement('img'); img.src = dataUrl; img.style.display = 'block'; img.style.maxWidth = '100%'; img.style.height = 'auto'; img.contentEditable = 'false'; img.draggable = false; if (activeEditorRef.current) { activeEditorRef.current.appendChild(img); updateStage(activeStageRef.current, 'body', activeEditorRef.current.innerHTML); } }; reader.readAsDataURL(file); e.target.value = ''; }} />
                      </div>
                    )}
                    
                    {loadingBody === stage && (
                      <p className="text-xs text-gray-500 mb-1">Loading your saved template…</p>
                    )}
                    {/* Rich text editor (contenteditable) */}
                    <BodyEditor
                      stage={stage}
                      body={data.body}
                      canEdit={canEdit}
                      emailStyle={emailStyles[stage] || DEFAULT_EMAIL_STYLE}
                      isExpanded={isExpanded && stage === expandedStage}
                      onUpdate={(html) => updateStage(stage, 'body', html)}
                      onFocus={() => { activeStageRef.current = stage; }}
                      setActiveEditorRef={(el) => { activeEditorRef.current = el; }}
                      onPaste={(e) => handlePaste(e, stage)}
                      onSelectImage={(img, additive) => selectTemplateImage(img, stage, additive)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Custom templates — per-user; every logged-in BDE can manage their own. */}
      {user?.id && (
        <div className="mt-10 pt-8 border-t border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">My templates</h2>
          <p className="text-sm text-gray-600 mb-4">Your own named templates. They only show up for you when sending emails.</p>
          {addingCustom ? (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
              <input
                placeholder="Template name (required)"
                value={customForm.name}
                onChange={(e) => setCustomForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <input
                placeholder="Subject"
                value={customForm.subject}
                onChange={(e) => setCustomForm(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <RichEmailEditor
                value={customForm.body}
                onChange={(html) => setCustomForm(prev => ({ ...prev, body: html }))}
                minHeight={200}
                placeholder="Body — paste anything, formatting and images are kept. Use {{first_name}} and {{company_name}}."
              />
              <input
                placeholder="Header (optional)"
                value={customForm.header}
                onChange={(e) => setCustomForm(prev => ({ ...prev, header: e.target.value }))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <SignatureImageField
                value={customForm.signature_image_url}
                onChange={(v) => setCustomForm(prev => ({ ...prev, signature_image_url: v || '' }))}
                disabled={saving}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={createCustomTemplate}
                  disabled={saving}
                  className="px-3 py-1.5 bg-[#679632] text-white rounded text-sm disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAddingCustom(false); setCustomForm({ name: '', subject: '', body: '', header: '', signature_image_url: '' }); }}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddingCustom(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-[#679632] text-[#679632] rounded-lg text-sm hover:bg-[#679632]/10 mb-4"
            >
              <Plus className="w-4 h-4" /> Add template
            </button>
          )}
          <ul className="space-y-2">
            {customTemplates.map((ct) => {
              return (
                <li key={ct.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedCustomId(prev => prev === ct.id ? null : ct.id)}
                  >
                    <span className="font-medium text-gray-800">{ct.name || 'Unnamed'}</span>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => deleteCustomTemplate(ct.id)}
                        disabled={saving}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedCustomId === ct.id ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expandedCustomId === ct.id && (
                    <CustomTemplateEditForm
                      template={ct}
                      saving={saving}
                      onSave={(payload) => updateCustomTemplate(ct.id, payload)}
                      onCancel={() => setExpandedCustomId(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
